import { useCallback, useEffect, useState } from "react";
import AutomationService from "../services/AutomationService";

/** Datos reales observados en la última ejecución: estructura del trigger y outputs por nodo. */
export interface LastExecutionData {
  executionId: string;
  enqueuedAtUtc: string;
  /** Payload del trigger (evento WorkflowStarted), si el backend lo registró. */
  trigger?: unknown;
  /** Output de cada nodo completado (evento NodeCompleted), por id de nodo. */
  nodeOutputs: Record<string, unknown>;
}

const service = new AutomationService();

function safeParse(json?: string | null): unknown {
  if (!json) return undefined;
  try {
    return JSON.parse(json);
  } catch {
    return undefined;
  }
}

/**
 * Carga los logs de la ejecución más reciente y los proyecta como muestras de datos
 * para el picker de expresiones del editor ("qué campos existen realmente").
 */
export function useLastExecutionData(workflowId: string, enabled: boolean) {
  const [data, setData] = useState<LastExecutionData | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const executions = await service.GetExecutions(workflowId, 10);
      // La más reciente con logs útiles; las ejecuciones vienen newest-first.
      for (const execution of executions) {
        const events = await service.GetExecutionLogs(workflowId, execution.executionId);
        if (events.length === 0) continue;

        const nodeOutputs: Record<string, unknown> = {};
        let trigger: unknown;
        for (const event of events) {
          if (event.eventType === "WorkflowStarted") {
            const payload = safeParse(event.payloadJson) as { triggerPayload?: unknown } | undefined;
            trigger = payload?.triggerPayload;
          } else if (event.eventType === "NodeCompleted" && event.nodeId) {
            const output = safeParse(event.payloadJson);
            if (output !== undefined) nodeOutputs[event.nodeId] = output;
          }
        }

        if (trigger !== undefined || Object.keys(nodeOutputs).length > 0) {
          setData({
            executionId: execution.executionId,
            enqueuedAtUtc: execution.enqueuedAtUtc,
            trigger,
            nodeOutputs,
          });
          return;
        }
      }
      setData(null);
    } catch {
      // error handled by ApiAcces interceptor
    } finally {
      setLoading(false);
    }
  }, [workflowId, enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, refresh };
}
